import { apiClient } from './api';
import { ListPendingCouriersResponse, ListAllCouriersResponse, Courier } from '../types/couriers';
import { db } from './firebase';
import { collection, query, where, getDocs, orderBy, onSnapshot } from 'firebase/firestore';

export function subscribeToCouriers(
    activeTab: 'onboarding' | 'fleet',
    onUpdate: (couriers: Courier[]) => void,
    onError: (error: any) => void
): () => void {
    let q;
    if (activeTab === 'onboarding') {
        q = query(
            collection(db, 'couriers'),
            where('status', '==', 'pendiente'),
            orderBy('createdAt', 'desc')
        );
    } else {
        q = query(collection(db, 'couriers'), orderBy('createdAt', 'desc'));
    }

    return onSnapshot(
        q,
        (snapshot) => {
            const couriers = snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as Courier));
            onUpdate(couriers);
        },
        (error) => {
            console.error(`Error subscribing to ${activeTab}:`, error);
            onError(error);
        }
    );
}

// Migrado a Firestore
export async function listPendingCouriers(): Promise<ListPendingCouriersResponse> {
    try {
        const q = query(
            collection(db, 'couriers'),
            where('status', '==', 'pendiente'),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        const couriers = snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as Courier));

        return {
            success: true,
            couriers: couriers,
            count: couriers.length
        } as ListPendingCouriersResponse;
    } catch (error) {
        console.error("Error listing pending couriers:", error);
        return { success: false, couriers: [], count: 0 };
    }
}

export async function approveCourier(uid: string) {
    const response = await apiClient.post('/approveCourier', { uid });
    return response.data;
}

export async function rejectCourier(uid: string) {
    const response = await apiClient.post('/rejectCourier', { uid });
    return response.data;
}

// Migrado a Firestore
export async function listAllCouriers(params: { status?: string; limit?: number; offset?: number } = {}): Promise<ListAllCouriersResponse> {
    try {
        let q = query(collection(db, 'couriers'), orderBy('createdAt', 'desc'));

        if (params.status) {
            q = query(q, where('status', '==', params.status));
        }

        const snapshot = await getDocs(q);
        const couriers = snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as Courier));

        return {
            success: true,
            couriers,
            pagination: { total: couriers.length, limit: params.limit || 100, offset: params.offset || 0, hasMore: false }
        };
    } catch (error) {
        console.error("Error listing all couriers:", error);
        return { success: false, couriers: [], pagination: { total: 0, limit: 100, offset: 0, hasMore: false } };
    }
}

export async function setCourierStatus(uid: string, status: string) {
    const response = await apiClient.put('/setCourierStatus', { uid, status });
    return response.data;
}

export async function updateCourierWallet(uid: string, wallet: { creditLimit: number; cashBalance?: number }) {
    const response = await apiClient.put('/updateCourierWallet', { uid, wallet });
    return response.data;
}

// Obtener un repartidor por UID
export async function getCourierById(uid: string): Promise<{ success: boolean; courier: Courier | null }> {
    try {
        const { doc, getDoc } = await import('firebase/firestore');
        const docRef = doc(db, 'couriers', uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                success: true,
                courier: { uid: docSnap.id, ...docSnap.data() } as Courier
            };
        } else {
            return { success: false, courier: null };
        }
    } catch (error) {
        console.error("Error getting courier by ID:", error);
        return { success: false, courier: null };
    }
}

export const couriersService = {
    listPending: listPendingCouriers,
    approve: approveCourier,
    reject: rejectCourier,
    listAll: listAllCouriers,
    setStatus: setCourierStatus,
    updateWallet: updateCourierWallet,
    subscribe: subscribeToCouriers,
    getById: getCourierById
};
